import { NetworkErrorService } from '@app/services/NetworkError.service';
import { ErrorHandlerService } from '@app/services/ErrorHandler.service';
import { PropertyGroupApi } from '@app/services/apiServices/PropertyGroupApi';
import { SpinnerService } from '@app/services/spinner.service';
import { Property } from "@app/models/Property";
import { PropertyGroupCandidate } from "@app/models/PropertyGroupCandidate";
import { PropertyApi } from "@app/services/apiServices/PropertyApi";
import { UserStore } from "@app/services/stores/UserStore";
import { take } from "rxjs/operators";
import { Component, Inject, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { DialogErrorDialog } from "@app/components/sharedDialogs/dialogError.dialog";
import { OneButtonDialogComponent } from "@app/components/sharedDialogs/oneButtonDialog.component";
import { ActivatedRoute, Router } from "@angular/router";

@Component({
    selector: 'app-create-property-group',
    template: require('./createpropertygroup.html')
})
export class CreatePropertyGroupComponent implements OnInit{

    propertyId: number;
    property: Property;
    propertyGroupNicknameError: boolean = false;
    nickname: string;
    groupCandidates: PropertyGroupCandidate[];
    sortedGroupCandidates: PropertyGroupCandidate[]
    doneLoading: boolean;
    staticPmcCouponId: string;
    staticPmcName: string;
    $stateParams: any;

    constructor(@Inject(MatDialog) private dialog: MatDialog,
                @Inject(PropertyApi) private propertyApi: PropertyApi,
                @Inject(PropertyGroupApi) private propertyGroupApi: PropertyGroupApi,
                @Inject(NetworkErrorService) private networkErrorService: NetworkErrorService,
                @Inject(ErrorHandlerService) private errorHandlerService: ErrorHandlerService,
                @Inject(SpinnerService) private spinnerService: SpinnerService,
                @Inject(UserStore) private userStore: UserStore,
                @Inject(Router) private router: Router,
                @Inject(ActivatedRoute) private route: ActivatedRoute) {

        if (this.router.getCurrentNavigation()) {
            this.$stateParams = this.router.getCurrentNavigation().extras.state;
        }

    }

    ngOnInit() {

        if (this.$stateParams) {
            this.nickname = this.$stateParams.nickname;
        }

        if (this.route.snapshot.params.propertyId) {
            this.spinnerService.showSpinner();
            this.propertyId = this.route.snapshot.params.propertyId;
            this.loadPropertyData().then(() => {
                this.spinnerService.closeSpinner();
                this.doneLoading = true;
            }, () => {
                this.spinnerService.closeSpinner();
                this.cancel();
            });
        }

    }

    loadPropertyData() {
        return Promise.all([
            this.loadProperty(),
            this.loadPropertyGroupCandidates()
        ]);
    }

    loadPropertyGroupCandidates(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            let userId = this.userStore.getHoaHomeownerId();
            this.propertyGroupApi.getPropertyGroupCandidates(userId, this.propertyId)
                .pipe(take(1)).subscribe((candidateResp) => {
                    this.groupCandidates = candidateResp;
                    this.sortGroupCandidates();
                    resolve();
                }, (err) => {
                    this.networkErrorService.handleNetworkError(err, true)
                        .pipe(take(1)).subscribe((resp) => {
                            if (resp === true) {
                                this.loadPropertyGroupCandidates();
                            }
                            reject();
                        });
                });
        });
    }

    loadProperty(): Promise<void> {
        return new Promise<void>((resolve, reject) => {

            this.propertyApi.propertiesIdGet(this.propertyId)
                .pipe(take(1)).subscribe((resp) => {
                    this.property = resp;
                    if(!this.nickname) {
                        this.nickname = this.property.address.line1;
                    }
                    this.staticPmcCouponId = this.property.hoa.pmc.couponId;
                    this.staticPmcName = this.property.hoa.pmc.name;
                    resolve();
                }, (err) => {
                    this.networkErrorService.handleNetworkError(err, true)
                        .pipe(take(1)).subscribe((resp) => {
                            if (resp === true) {
                                this.loadProperty();
                            }
                            reject();
                        });
                });

        });
    }

    addProperty(registeredPropertyId: number) {
        const propertyIds: number[] = [this.propertyId, registeredPropertyId];
        this.propertyGroupApi.createPropertyGroup(this.nickname, propertyIds)
            .pipe(take(1)).subscribe((resp) => {
                if(resp.status === 200) {
                    let propertyGroup = resp.body;
                    this.router.navigateByUrl('/properties/propertyGroup/' + propertyGroup.id,
                        { state: { propertyGroupId: propertyGroup.id } });
                } else {
                    this.router.navigateByUrl('/properties');
                }
            }, () => {
                this.router.navigateByUrl('/properties');
            });
    }

    register(groupCandidate: PropertyGroupCandidate) {
        this.spinnerService.showSpinner();

        this.propertyGroupApi.getHoa(groupCandidate.couponData)
            .pipe(take(1)).subscribe((resp) => {
                if(resp.status === 200) {
                    let hoa = resp.body;

                    const newProperty: Property = {
                        couponId: groupCandidate.couponData.propertyCouponId,
                        hoa: hoa,
                        address: this.property.address,
                        nickname: groupCandidate.nickname
                    }
                    let userId = this.userStore.getHoaHomeownerId();

                    this.propertyApi.homeownersIdPropertiesPost(false, userId, newProperty)
                        .pipe(take(1)).subscribe({ next: (response) => {
                            if(response.status === 200) {
                                this.addProperty(response.body.id);
                            }
                        }, error: err => {
                            let errorMessage = err.error.message;
                            if (err.error.errors && err.error.errors.length > 0) {
                                err.error.errors.forEach(msg => {
                                    errorMessage += '<br><br>' + msg;
                                })
                            }
                            this.errorHandlerService.handleNetworkError(err, true, errorMessage)
                                .pipe().subscribe(() =>  {
                                    this.spinnerService.closeSpinner()
                                })
                        } })
                }
            },  (err) => {

                const dialogRef = this.dialog.open(DialogErrorDialog);
                dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
                    this.spinnerService.closeSpinner();
                    this.networkErrorService.handleNetworkError(err, true)
                        .pipe(take(1)).subscribe((resp) => {
                            if (resp === true) {
                                this.register(groupCandidate);
                            } else {
                                this.router.navigateByUrl('/properties');
                            }
                        });
                })
            });

    }

    cancel() {
        this.router.navigateByUrl('/properties');
    }

    invalid() {
        return !this.nickname;
    }


    addPropertyHelper() {
        this.dialog.open(OneButtonDialogComponent, {
            data: {
                "header": "Add Account",
                "message": "Add the currently registered property to the group",
                "buttonText": "Close"
            }
        });
    }

    groupNameHelper() {
        this.dialog.open(OneButtonDialogComponent, {
            data: {
                "header": "Group Nickname",
                "message": "Provide a name for the related accounts group.  This will be the name displayed on the " +
                    "home page that can be expanded to show the accounts in the group.",
                "message2": "Defaults to the first line of the address.",
                "buttonText": "Close"
            }
        });
    }

    registerPropertyHelper() {
        this.dialog.open(OneButtonDialogComponent, {
            data: {
                "header": "Register Account",
                "message": "Registers the property and adds it to the group",
                "buttonText": "Close"
            }
        });
    }

    private sortGroupCandidates() {
        this.sortedGroupCandidates = [];

        if (!this.groupCandidates || this.groupCandidates.length === 0) {
            return;
        } else if (this.groupCandidates.length === 1) {
            this.sortedGroupCandidates.push(this.groupCandidates[0])
            return
        }

        // orderBy: propertyId, couponData.hoaCouponId, couponData.propertyCouponId
        this.sortedGroupCandidates = this.groupCandidates.sort((a, b) => {

            let returnValue = b.propertyId - a.propertyId;

            if (returnValue === 0) {

                if (a.couponData.hoaCouponId < b.couponData.hoaCouponId) {
                    returnValue = -1
                } else if (a.couponData.hoaCouponId > b.couponData.hoaCouponId) {
                    returnValue = 1
                }
            }

            if (returnValue === 0) {
                if (a.couponData.propertyCouponId < b.couponData.propertyCouponId) {
                    returnValue = -1
                } else if (a.couponData.propertyCouponId > b.couponData.propertyCouponId) {
                    returnValue = 1
                }
            }

            return returnValue;
        })

    }
}

